import { API_URL } from 'app.config';
import theAxios from 'axios';

/**
 * Defensive Axios instance:
 * - On the server, require NEXT_PUBLIC_URL_API to be set (fail-fast) to avoid silent requests to localhost:80
 * - On the client, fall back to relative requests when NEXT_PUBLIC_URL_API is not provided
 */
const baseURL = API_URL || (typeof window !== 'undefined' ? '' : undefined);
if (!baseURL && typeof window === 'undefined') {
  // Fail-fast on server to avoid attempting requests against localhost:80
  throw new Error(
    'Missing API base URL: `NEXT_PUBLIC_URL_API` is not defined. Server-side API requests require this env var.'
  );
}

const Axios = theAxios.create({
  baseURL: baseURL,
  // keep other sensible defaults here if needed
});

// Normalize request URLs and avoid mutating absolute URLs
Axios.interceptors.request.use((config) => {
  if (!config || !config.url) return config;
  const url = String(config.url);
  const isAbsolute = /^https?:\/\//i.test(url) || /^\/\//.test(url);
  if (!isAbsolute) {
    // preserve query string when adding trailing slash
    const [path, query = ''] = url.split('?');
    if (!path.endsWith('/')) {
      config.url = query ? `${path}/${'?' + query}` : `${path}/`;
    }
  }
  return config;
});

// Provide clearer error logs for common failures (missing baseURL, 404 during dev, etc.)
Axios.interceptors.response.use(
  (res) => res,
  (error) => {
    // annotate common scenarios to make debugging easier in dev
    if (error?.config && !Axios.defaults.baseURL) {
      // only warn (not throw) on client
      if (typeof window !== 'undefined') {
        // eslint-disable-next-line no-console
        console.warn(
          '[Axios] requests are being sent without an API baseURL — set NEXT_PUBLIC_URL_API to avoid requests hitting the current origin.'
        );
      }
    }

    // Attach a short, consistent code for downstream error-handling
    if (error?.response?.status === 404) {
      error.code = error.code || 'API_NOT_FOUND';
    }

    return Promise.reject(error);
  }
);

export const configureAxiosLocale = (locale: string = 'en') => {
  Axios.defaults.headers.common['Content-Language'] = locale;
  Axios.defaults.headers.common['Accept-Language'] = locale;
};

export const configureAxiosAuth = (token?: string) => {
  Axios.defaults.headers.common['Authorization'] = token ? 'Bearer ' + token : '';
};

export default Axios;
